import React, { useState } from 'react';
import {
  ArrowRight,
  CaretDown,
  CaretUp,
  ChatText,
  CheckCircle,
  Clock,
  DownloadSimple,
  Export,
  FileText,
  FlowArrow,
  GearSix,
  Info,
  Lightbulb,
  Lightning,
  Play,
  Shield,
  Stack,
  Target,
  UploadSimple,
  UsersThree,
  WarningCircle
} from '@phosphor-icons/react';

/**
 * Collapsible Section Component
 */
const CollapsibleSection = ({ title, icon: Icon, iconColor, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="glass rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={18} className={iconColor} />
          <h3 className="text-sm font-semibold text-white uppercase">{title}</h3>
        </div>
        {isOpen ? (
          <CaretUp size={18} className="text-gray-400" />
        ) : (
          <CaretDown size={18} className="text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-0">
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * Expandable Analysis Steps Component
 */
const ExpandableAnalysisSteps = ({ steps }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const VISIBLE_COUNT = 4;
  const hasMore = steps?.length > VISIBLE_COUNT;
  
  const visibleSteps = isExpanded ? steps : steps?.slice(0, VISIBLE_COUNT);
  
  return (
    <div className="space-y-2">
      {visibleSteps?.map((step, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <span className="text-[10px] text-[#8B5CF6] font-bold mt-0.5">{idx + 1}.</span>
          <p className={`text-xs text-[#A0AEC0] ${!isExpanded ? 'line-clamp-2' : ''}`}>{step}</p>
        </div>
      ))}
      {hasMore && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-[10px] text-[#8B5CF6] hover:text-[#A78BFA] font-medium transition-colors flex items-center gap-1 mt-1"
        >
          {isExpanded ? (
            <>
              <CaretUp size={12} />
              Show less
            </>
          ) : (
            <>
              <CaretDown size={12} />
              +{steps.length - VISIBLE_COUNT} more steps
            </>
          )}
        </button>
      )}
    </div>
  );
};

/**
 * IAODisplay Component
 * Renders the full OpportunitySpec V1 with business clarity sections
 */
const IAODisplay = ({ opportunity }) => {
  const iao = opportunity?.iao;
  const betaTruth = opportunity?.beta_truth;
  const integrationReq = opportunity?.integration_requirements;
  const guardrails = opportunity?.guardrails;
  const expectedOutcomes = opportunity?.expected_outcomes;
  const workflowFit = opportunity?.workflow_fit;
  const exampleInputs = opportunity?.example_inputs;
  const exampleOutputs = opportunity?.example_outputs;
  const confidenceNotes = opportunity?.confidence_notes;
  
  if (!iao) {
    return null;
  }

  // Format input type for display
  const formatInputType = (inputType) => {
    const typeMap = {
      'text': 'Text',
      'file_pdf': 'PDF',
      'file_docx': 'DOCX',
      'file_csv': 'CSV',
      'file_xlsx': 'Excel',
      'image': 'Image',
      'audio_file': 'Audio',
      'video_file': 'Video',
      'phone_call': 'Phone Call',
      'webhook': 'Webhook'
    };
    return typeMap[inputType] || inputType;
  };

  // Format output type for display
  const formatOutputType = (outputType) => {
    const typeMap = {
      'markdown': 'Markdown',
      'file_docx': 'DOCX',
      'file_pdf': 'PDF',
      'file_csv': 'CSV',
      'file_xlsx': 'Excel',
      'webhook_payload': 'Webhook',
      'call_transcript': 'Transcript',
      'call_summary': 'Call Summary'
    };
    return typeMap[outputType] || outputType;
  };

  return (
    <div className="space-y-4">
      {/* One-liner Banner */}
      {opportunity?.one_liner && (
        <div className="glass rounded-xl p-4 border-l-4 border-[#6366F1]">
          <p className="text-white font-medium">{opportunity.one_liner}</p>
        </div>
      )}

      {/* Section 1: Expected Outcomes (Always visible) */}
      {Array.isArray(expectedOutcomes) && expectedOutcomes.length > 0 && (
        <div className="glass rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target size={18} className="text-[#14F195]" />
            <h3 className="text-sm font-semibold text-white uppercase">Expected Outcomes</h3>
          </div>
          <div className="space-y-3">
            {expectedOutcomes.map((outcome, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[rgba(20,241,149,0.2)] flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={14} className="text-[#14F195]" />
                </div>
                <p className="text-sm text-white leading-relaxed">{outcome}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 2: How It Works (IAO Flow) - Default Open */}
      <CollapsibleSection 
        title="How It Works" 
        icon={FlowArrow} 
        iconColor="text-[#8B5CF6]"
        defaultOpen={true}
      >
        <div className="grid md:grid-cols-3 gap-4">
          {/* INPUT */}
          <div className="bg-[rgba(99,102,241,0.1)] rounded-xl p-4 border border-[rgba(99,102,241,0.2)]">
            <div className="flex items-center gap-2 mb-3">
              <UploadSimple size={16} className="text-[#6366F1]" />
              <span className="text-xs font-semibold text-[#6366F1] uppercase">Input</span>
            </div>
            
            <div className="flex flex-wrap gap-1.5 mb-3">
              {Array.isArray(iao.input?.accepted_inputs) && iao.input.accepted_inputs.map((input, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 rounded-lg bg-[rgba(99,102,241,0.2)] text-[#818CF8] text-xs font-medium"
                >
                  {formatInputType(input)}
                </span>
              ))}
            </div>

            {iao.input?.trigger && (
              <p className="text-xs text-[#A0AEC0]">
                <span className="text-[#718096]">Trigger:</span> {iao.input.trigger}
              </p>
            )}
          </div>

          {/* ANALYSIS */}
          <div className="bg-[rgba(139,92,246,0.1)] rounded-xl p-4 border border-[rgba(139,92,246,0.2)]">
            <div className="flex items-center gap-2 mb-3">
              <FlowArrow size={16} className="text-[#8B5CF6]" />
              <span className="text-xs font-semibold text-[#8B5CF6] uppercase">Analysis</span>
            </div>
            
            <ExpandableAnalysisSteps steps={iao.analysis?.steps} />
          </div>

          {/* OUTPUT */}
          <div className="bg-[rgba(20,241,149,0.1)] rounded-xl p-4 border border-[rgba(20,241,149,0.2)]">
            <div className="flex items-center gap-2 mb-3">
              <DownloadSimple size={16} className="text-[#14F195]" />
              <span className="text-xs font-semibold text-[#14F195] uppercase">Output</span>
            </div>
            
            <div className="flex flex-wrap gap-1.5 mb-3">
              {Array.isArray(iao.output?.outputs_declared) && iao.output.outputs_declared.map((output, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 rounded-lg bg-[rgba(20,241,149,0.2)] text-[#14F195] text-xs font-medium"
                >
                  {formatOutputType(output)}
                </span>
              ))}
            </div>

            {Array.isArray(iao.output?.primary_artifacts) && iao.output.primary_artifacts.length > 0 && (
              <div className="space-y-1">
                {iao.output.primary_artifacts.slice(0, 3).map((artifact, idx) => (
                  <p key={idx} className="text-xs text-[#A0AEC0] flex items-center gap-1">
                    <CheckCircle size={10} className="text-[#14F195]" />
                    {artifact}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 3: FlowArrow Fit */}
      {workflowFit && (
        <CollapsibleSection 
          title="FlowArrow Fit" 
          icon={UsersThree} 
          iconColor="text-[#F59E0B]"
          defaultOpen={false}
        >
          <div className="grid md:grid-cols-2 gap-4">
            {/* Primary UsersThree */}
            {Array.isArray(workflowFit.primary_users) && workflowFit.primary_users.length > 0 && (
              <div>
                <p className="text-xs text-[#718096] uppercase mb-2">Who Uses It</p>
                <div className="flex flex-wrap gap-2">
                  {workflowFit.primary_users.map((user, idx) => (
                    <span 
                      key={idx}
                      className="px-2 py-1 rounded-lg bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.3)] text-[#FBBF24] text-xs font-medium"
                    >
                      {user}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Where It Runs */}
            {workflowFit.where_it_runs && (
              <div>
                <p className="text-xs text-[#718096] uppercase mb-2">When/Where It Runs</p>
                <div className="flex items-start gap-2">
                  <Clock size={14} className="text-[#F59E0B] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-[#A0AEC0]">{workflowFit.where_it_runs}</p>
                </div>
              </div>
            )}

            {/* Trigger */}
            {workflowFit.trigger && (
              <div>
                <p className="text-xs text-[#718096] uppercase mb-2">Trigger</p>
                <div className="flex items-start gap-2">
                  <Play size={14} className="text-[#14F195] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-[#A0AEC0]">{workflowFit.trigger}</p>
                </div>
              </div>
            )}

            {/* Handoff */}
            {workflowFit.handoff && (
              <div>
                <p className="text-xs text-[#718096] uppercase mb-2">After Completion</p>
                <div className="flex items-start gap-2">
                  <ArrowRight size={14} className="text-[#6366F1] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-[#A0AEC0]">{workflowFit.handoff}</p>
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Section 4: Examples */}
      {(Array.isArray(exampleInputs) && exampleInputs.length > 0) || (Array.isArray(exampleOutputs) && exampleOutputs.length > 0) ? (
        <CollapsibleSection 
          title="Examples" 
          icon={Lightbulb} 
          iconColor="text-[#EC4899]"
          defaultOpen={false}
        >
          <div className="grid md:grid-cols-2 gap-6">
            {/* Example Inputs */}
            {Array.isArray(exampleInputs) && exampleInputs.length > 0 && (
              <div>
                <p className="text-xs text-[#718096] uppercase mb-3 flex items-center gap-1">
                  <UploadSimple size={12} />
                  Example Inputs
                </p>
                <ul className="space-y-2">
                  {exampleInputs.map((example, idx) => (
                    <li key={idx} className="text-sm text-[#A0AEC0] flex items-start gap-2 pl-2 border-l-2 border-[#6366F1]/30">
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Example Outputs */}
            {Array.isArray(exampleOutputs) && exampleOutputs.length > 0 && (
              <div>
                <p className="text-xs text-[#718096] uppercase mb-3 flex items-center gap-1">
                  <DownloadSimple size={12} />
                  Example Outputs
                </p>
                <ul className="space-y-2">
                  {exampleOutputs.map((example, idx) => (
                    <li key={idx} className="text-sm text-[#A0AEC0] flex items-start gap-2 pl-2 border-l-2 border-[#14F195]/30">
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CollapsibleSection>
      ) : null}

      {/* Section 5: Assumptions & Confidence */}
      {(confidenceNotes || opportunity?.roi_assumptions) && (
        <CollapsibleSection 
          title="Assumptions & Confidence" 
          icon={Info} 
          iconColor="text-[#718096]"
          defaultOpen={false}
        >
          {confidenceNotes && (
            <div className="mb-4 p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
              <p className="text-sm text-[#A0AEC0] leading-relaxed">{confidenceNotes}</p>
            </div>
          )}
          
          {opportunity?.roi_assumptions && (
            <div className="space-y-2">
              {opportunity.roi_assumptions.hours_saved_basis && (
                <p className="text-xs text-[#718096]">
                  <span className="text-[#A0AEC0]">Hours Saved Basis:</span> {opportunity.roi_assumptions.hours_saved_basis}
                </p>
              )}
              {opportunity.roi_assumptions.hourly_rate_source && (
                <p className="text-xs text-[#718096]">
                  <span className="text-[#A0AEC0]">Rate Source:</span> {opportunity.roi_assumptions.hourly_rate_source}
                </p>
              )}
              {opportunity.roi_assumptions.confidence_level && (
                <p className="text-xs text-[#718096]">
                  <span className="text-[#A0AEC0]">Confidence Level:</span> {opportunity.roi_assumptions.confidence_level}
                </p>
              )}
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Integrations & Beta Truth */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Integrations */}
        {integrationReq && (Array.isArray(integrationReq.required) && integrationReq.required.length > 0 || Array.isArray(integrationReq.optional) && integrationReq.optional.length > 0) && (
          <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <GearSix size={18} className="text-[#6366F1]" />
              <h3 className="text-sm font-semibold text-white uppercase">Integrations</h3>
            </div>

            {Array.isArray(integrationReq.required) && integrationReq.required.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-[#718096] uppercase mb-2">Required</p>
                <div className="flex flex-wrap gap-2">
                  {integrationReq.required.map((integration, idx) => (
                    <span 
                      key={idx}
                      className="px-2 py-1 rounded-lg bg-[rgba(236,72,153,0.1)] border border-[rgba(236,72,153,0.3)] text-[#F472B6] text-xs font-medium"
                    >
                      {integration}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(integrationReq.optional) && integrationReq.optional.length > 0 && (
              <div>
                <p className="text-xs text-[#718096] uppercase mb-2">Optional</p>
                <div className="flex flex-wrap gap-2">
                  {integrationReq.optional.map((integration, idx) => (
                    <span 
                      key={idx}
                      className="px-2 py-1 rounded-lg bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.3)] text-[#818CF8] text-xs font-medium"
                    >
                      {integration}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Beta Truth */}
        {betaTruth && (Array.isArray(betaTruth.does_not) && betaTruth.does_not.length > 0 || Array.isArray(betaTruth.requires_integration_for) && betaTruth.requires_integration_for.length > 0) && (
          <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <WarningCircle size={18} className="text-[#F59E0B]" />
              <h3 className="text-sm font-semibold text-white uppercase">Important Notes</h3>
            </div>

            {Array.isArray(betaTruth.does_not) && betaTruth.does_not.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-[#718096] uppercase mb-2">This Agent Does NOT</p>
                <ul className="space-y-1">
                  {betaTruth.does_not.map((item, idx) => (
                    <li key={idx} className="text-sm text-[#F59E0B] flex items-start gap-2">
                      <span>⚠</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(betaTruth.requires_integration_for) && betaTruth.requires_integration_for.length > 0 && (
              <div>
                <p className="text-xs text-[#718096] uppercase mb-2">Requires Integration For</p>
                <ul className="space-y-1">
                  {betaTruth.requires_integration_for.map((item, idx) => (
                    <li key={idx} className="text-sm text-[#A0AEC0] flex items-start gap-2">
                      <GearSix size={14} className="text-[#6366F1] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quality Flags Warning */}
      {opportunity?.quality_flags?.is_vague && Array.isArray(opportunity.quality_flags.vague_reasons) && opportunity.quality_flags.vague_reasons.length > 0 && (
        <div className="glass rounded-xl p-4 border border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.1)]">
          <div className="flex items-center gap-2 mb-2">
            <WarningCircle size={18} className="text-[#F59E0B]" />
            <h4 className="text-sm font-semibold text-[#F59E0B]">Quality Warning</h4>
          </div>
          <p className="text-sm text-[#A0AEC0] mb-2">
            This opportunity may contain vague language and could benefit from more specific details:
          </p>
          <ul className="space-y-1">
            {opportunity.quality_flags.vague_reasons.map((reason, idx) => (
              <li key={idx} className="text-sm text-[#F59E0B] flex items-start gap-2">
                <span>•</span>
                {reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default IAODisplay;
